/**
 * Mapeamento de status do job → linguagem clara para usuários não técnicos
 * (R19.4/R19.6) e definição das etapas visuais do acompanhamento (stepper).
 *
 * Os status vêm de `GET /jobs/{id}` (`makepub_shared.models.JobStatus`):
 *   RECEIVED → PARSING → GENERATING → VALIDATING → (REPAIRING → VALIDATING)*
 *            → DONE | FAILED
 *
 * A UI não expõe esses nomes crus. Ela agrupa os status em quatro etapas
 * observáveis pelo usuário — Recebido, Processando, Validando, Concluído — e
 * descreve cada uma em português simples. `REPAIRING` é apresentado como parte
 * de "Validando" (o sistema está ajustando o resultado), sem jargão de "reparo".
 */

/** Status crus retornados pela API (contrato estável do backend). */
export const JOB_STATUSES = [
  "RECEIVED",
  "PARSING",
  "GENERATING",
  "VALIDATING",
  "REPAIRING",
  "DONE",
  "FAILED",
] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

/** Etapas visíveis ao usuário no acompanhamento (linha do tempo). */
export const STAGES = ["received", "processing", "validating", "done"] as const;
export type Stage = (typeof STAGES)[number];

export interface StageInfo {
  readonly id: Stage;
  readonly label: string;
  readonly description: string;
}

/** Definição ordenada das etapas exibidas no stepper. */
export const STAGE_DEFS: readonly StageInfo[] = [
  {
    id: "received",
    label: "Recebido",
    description: "Recebemos seu documento e a fila de processamento foi iniciada.",
  },
  {
    id: "processing",
    label: "Processando",
    description: "Estamos lendo o conteúdo e montando o seu EPUB.",
  },
  {
    id: "validating",
    label: "Validando",
    description: "Conferindo qualidade e acessibilidade, ajustando se necessário.",
  },
  {
    id: "done",
    label: "Concluído",
    description: "Seu EPUB está pronto para download.",
  },
];

/** Estado de progresso de cada etapa no stepper. */
export type StageState = "pending" | "active" | "complete" | "failed";

/** A etapa visível correspondente a um status cru da API. */
export function stageForStatus(status: JobStatus): Stage {
  switch (status) {
    case "RECEIVED":
      return "received";
    case "PARSING":
    case "GENERATING":
      return "processing";
    case "VALIDATING":
    case "REPAIRING":
      return "validating";
    case "DONE":
      return "done";
    case "FAILED":
      // A falha "mora" na etapa em que o usuário estava aguardando resultado.
      return "validating";
  }
}

/** `true` quando o status é terminal (não muda mais): DONE ou FAILED. */
export function isTerminal(status: JobStatus): boolean {
  return status === "DONE" || status === "FAILED";
}

/** `true` quando o job concluiu com sucesso. */
export function isDone(status: JobStatus): boolean {
  return status === "DONE";
}

/** `true` quando o job falhou. */
export function isFailed(status: JobStatus): boolean {
  return status === "FAILED";
}

export interface PlainStatus {
  /** Título curto em pt-BR (ex.: "Processando seu documento"). */
  readonly title: string;
  /** Frase de apoio explicando o momento atual. */
  readonly detail: string;
  /** Tom para estilo/ícone da UI. */
  readonly tone: "info" | "progress" | "success" | "error";
}

/**
 * Descrição em linguagem clara do status atual (R19.4/R19.6).
 *
 * `attemptCount` (quando > 1) é usado para explicar que o sistema está fazendo
 * novos ajustes, sem expor a mecânica interna do loop de reparo.
 */
export function plainStatus(status: JobStatus, attemptCount = 0): PlainStatus {
  switch (status) {
    case "RECEIVED":
      return {
        title: "Documento recebido",
        detail: "Seu documento entrou na fila. Vamos começar em instantes.",
        tone: "info",
      };
    case "PARSING":
      return {
        title: "Lendo seu documento",
        detail: "Estamos interpretando o conteúdo, a estrutura e as imagens.",
        tone: "progress",
      };
    case "GENERATING":
      return {
        title: "Montando o EPUB",
        detail: "Gerando o livro digital com tipografia e navegação adequadas.",
        tone: "progress",
      };
    case "VALIDATING":
      return {
        title: "Conferindo a qualidade",
        detail: "Validando conformidade e acessibilidade do EPUB gerado.",
        tone: "progress",
      };
    case "REPAIRING":
      return {
        title: "Fazendo ajustes finais",
        detail:
          attemptCount > 1
            ? `Encontramos pontos a melhorar e estamos ajustando (tentativa ${attemptCount}).`
            : "Encontramos pontos a melhorar e estamos ajustando automaticamente.",
        tone: "progress",
      };
    case "DONE":
      return {
        title: "EPUB pronto",
        detail: "Seu documento foi convertido com sucesso. Já pode baixar.",
        tone: "success",
      };
    case "FAILED":
      return {
        title: "Não foi possível concluir",
        detail: "Houve um problema na conversão. Veja os detalhes abaixo.",
        tone: "error",
      };
  }
}

/**
 * Calcula o estado de cada etapa do stepper dado o status atual.
 * Retorna um mapa etapa → estado ("complete"/"active"/"pending"/"failed").
 */
export function stageStates(status: JobStatus): Record<Stage, StageState> {
  const current = stageForStatus(status);
  const currentIndex = STAGES.indexOf(current);
  const failed = isFailed(status);
  const done = isDone(status);

  const result = {} as Record<Stage, StageState>;
  STAGES.forEach((stage, index) => {
    if (done) {
      result[stage] = "complete";
      return;
    }
    if (index < currentIndex) {
      result[stage] = "complete";
    } else if (index === currentIndex) {
      result[stage] = failed ? "failed" : "active";
    } else {
      result[stage] = "pending";
    }
  });
  return result;
}
